import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, ExternalLink, ShoppingCart, CreditCard } from "lucide-react";
import { PARTNER_LINK, DISCOUNT_CODE } from "@/lib/constants";


const steps = [
  {
    number: "1",
    icon: Copy,
    title: "Code kopieren",
    text: "Kopiere den heilys Rabattcode LONGEVITY über die Schaltfläche auf dieser Seite oder merke ihn dir für den nächsten Schritt.",
  },
  {
    number: "2",
    icon: ExternalLink,
    title: "heilys Shop öffnen",
    text: "Öffne den offiziellen heilys Shop über den Partnerlink auf dieser Seite.",
  },
  {
    number: "3",
    icon: ShoppingCart,
    title: "Produkte auswählen",
    text: "Stöbere im heilys Sortiment und lege die gewünschten Produkte in den Warenkorb.",
  },
  {
    number: "4",
    icon: CreditCard,
    title: "Code im Checkout einlösen",
    text: "Füge den Code LONGEVITY im Checkout-Bereich ein. Der Rabatt von 5 % wird automatisch abgezogen.",
  },
];

const HowToSection = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(DISCOUNT_CODE);
    setCopied(true);
    window.lh?.trackCodeCopy({ source: "howto" });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="anleitung" className="bg-card/50 py-10 md:py-20">
      <div className="container">
        <h2 className="text-center font-display text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
          So löst du den heilys Rabattcode & Gutscheincode ein
        </h2>
        <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-muted-foreground md:mt-3 md:text-base">
          Den heilys Gutschein bzw. Rabattcode LONGEVITY kannst du direkt im Checkout des offiziellen heilys Shops einlösen.
        </p>

        <div className="mx-auto mt-8 md:mt-10 grid max-w-4xl gap-4 sm:gap-5 grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0.4, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ delay: i * 0.06, duration: 0.3 }}
              className="relative rounded-xl border border-border/50 bg-card p-3 sm:p-5 text-center transition-all hover:border-primary/20"
            >
              <span className="font-display text-2xl sm:text-3xl font-extrabold text-primary/15" aria-hidden="true">
                {step.number}
              </span>
              <div className="mx-auto mt-1.5 sm:mt-2 inline-flex items-center justify-center rounded-lg bg-primary/10 p-1.5 sm:p-2">
                <step.icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" aria-hidden="true" />
              </div>
              <h3 className="mt-2 sm:mt-3 font-display text-xs sm:text-sm font-semibold text-foreground">
                {step.title}
              </h3>
              <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm leading-relaxed text-muted-foreground">{step.text}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3">
          <button
            onClick={handleCopy}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border/50 bg-card px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-secondary w-full sm:w-auto"
            aria-label="Rabattcode LONGEVITY kopieren"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 text-success" aria-hidden="true" />
                Kopiert!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" aria-hidden="true" />
                {DISCOUNT_CODE} kopieren
              </>
            )}
          </button>
          <a
            href={PARTNER_LINK}
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={() => window.lh?.trackAffiliate({ source: "howto" })}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:brightness-110 w-full sm:w-auto"
          >
            Zum heilys Shop
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowToSection;
